import {Component, useState, useRef, useCallback} from 'react'

import PropTypes from 'prop-types'

import {Cancel, Continue, ControlButton} from '../../../../../../shared_components/ButtonTypes'
import Form from '../../../../../../shared_components/forms/v2/Form'
import Modal from '../../../../../../shared_components/Modal'
import ShadowHeader from '../../ShadowHeader'
import StatusToggle from '../StatusToggle'
import bindUpdate from '../../../../../../shared_components/BoundUpdate'
import CobrandingActions from '../../../../../../shared_actions/CobrandingActions'
import modalWrapper, {MODAL_CONTEXT_TYPES} from '../../../../../../shared_components/ModalWrapper'
import EverplansBrandedFields from './EverplansBrandedFields'
import SharedCobrandingFields from './SharedCobrandingFields'
import CobrandingPreviewLink from '../CobrandingPreviewLink'
import ContactAccountManagerNote from '../ContactAccountManagerNote'
import ShadowWrapper from '../../ShadowWrapper'

const EditCobrandingContent = props => {
  const {
    cobranding,
    finished,
    firmId,
    isAccountManager,
    isFirmActive,
    isModalVisible,
    modalContext,
    processing,
    updating
  } = props

  const everplansBrandedFormRef = useRef(null)

  const [cobrandingEnabled, setCobrandingEnabled] = useState(
    cobranding.attributes['cobranding-enabled']
  )

  const [formUpdateCount, setFormUpdateCount] = useState(0)

  const toggleCobranding = useCallback(() => {
    setCobrandingEnabled(prev => !prev)
  }, [])

  const handleFormUpdate = useCallback(() => {
    setFormUpdateCount(prev => prev + 1)
  }, [])

  const haveSettingsBeenUpdated = useCallback(() => {
    const original = cobranding.attributes

    if (cobrandingEnabled !== original['cobranding-enabled']) return true

    const formRef = everplansBrandedFormRef.current

    if (!formRef) return false

    const formData = formRef.formData()

    if (!formData) return false

    const urlString = formData.getIn(['cobranding-url-string', 'value'])

    if ((urlString || null) !== (original['cobranding-url-string'] || null))
      return true

    const prefix = formData.getIn(['cobranding-prefix', 'value'])

    if ((prefix || null) !== (original['cobranding-prefix'] || null)) return true

    return false
  }, [cobrandingEnabled, cobranding.attributes, formUpdateCount])

  const onSubmit = useCallback(() => {
    updating(() => modalContext.closeModal())

    if (!cobrandingEnabled) {
      CobrandingActions.updateCobranding({
        id: cobranding.id,
        attributes: {'cobranding-enabled': false}
      })
    } else {
      const formRef = everplansBrandedFormRef.current

      const formData = formRef.onSubmit()

      if (formData) {
        CobrandingActions.updateCobranding({
          id: cobranding.id,
          attributes: {
            ...formData.map(data => data.get('value')).toJS(),
            'cobranding-enabled': true,
            style: 'everplans_branded'
          }
        })
      }
    }

    finished()
  }, [cobranding, cobrandingEnabled, finished, firmId, modalContext, updating])

  const resetState = useCallback(() => {
    setCobrandingEnabled(cobranding.attributes['cobranding-enabled'])

    modalContext.closeModal()
  }, [cobranding.attributes, modalContext])

  return (
    <ShadowWrapper>
      <div className='edit-cobranding settings row-wrapper'>
        <p>Status: </p>
        <div className='column-wrapper'>
          <p>
            {
              cobranding.attributes['cobranding-enabled'] ?
                'Everplans Cobranded' :
                'Disabled'
            }
          </p>
          <p className='description'>
            {cobranding.attributes['cobranding-enabled'] ?
              'Clients will get a cobranded Everplans experience.' :
              'Clients see the standard Everplans header and footer.'}
          </p>
          <CobrandingPreviewLink
            cobranding={cobranding}
            isAccountManager={isAccountManager}
          />
          {isFirmActive && isAccountManager && (
            <div className='edit-config'>
              <ControlButton
                faClass='fa-pencil-square-o'
                onClick={modalContext.showModal}
                buttonText='Edit'
              />
              <Modal visible={isModalVisible}>
                <ShadowHeader headerText='Cobranding' />
                <div className='config-wrapper'>
                  <StatusToggle
                    description={{
                      disabled: `Clients see the standard Everplans header and
                      footer.`,
                      enabled: 'Clients see a cobranded header and footer.'
                    }}
                    enabledChecked={cobrandingEnabled}
                    name='cobranding'
                    onChange={toggleCobranding}
                    statusText={{
                      disabled: 'Disabled',
                      enabled: 'Everplans Cobranded'
                    }}
                  />
                  {cobrandingEnabled && (
                    <section className='cobranding-form forms-playground'>
                      <Form
                        className='core side-labels'
                        onUpdate={handleFormUpdate}
                        ref={everplansBrandedFormRef}>
                        <SharedCobrandingFields
                          cobranding={cobranding.attributes}
                        />
                        <EverplansBrandedFields
                          cobranding={cobranding.attributes}
                        />
                      </Form>
                    </section>
                  )}
                  <div className='button-group'>
                    <Cancel onClick={resetState} />
                    <Continue
                      disabled={!haveSettingsBeenUpdated() || processing}
                      loading={processing}
                      onClick={onSubmit}>
                      Save
                    </Continue>
                  </div>
                </div>
              </Modal>
            </div>
          )}
          <ContactAccountManagerNote isAccountManager={isAccountManager} />
        </div>
      </div>
    </ShadowWrapper>
  )
}

EditCobrandingContent.propTypes = {
  cobranding: PropTypes.shape({
    attributes: PropTypes.shape({
      'cobranding-enabled': PropTypes.bool,
      'cobranding-url-string': PropTypes.string,
      'cobranding-prefix': PropTypes.string,
      style: PropTypes.string
    }),
    id: PropTypes.string
  }),
  finished: PropTypes.func,
  firmId: PropTypes.string.isRequired,
  isAccountManager: PropTypes.bool,
  isFirmActive: PropTypes.bool,
  isModalVisible: PropTypes.bool,
  modalContext: PropTypes.shape({
    closeModal: PropTypes.func,
    modalClosed: PropTypes.func,
    showModal: PropTypes.func
  }),
  processing: PropTypes.bool,
  updating: PropTypes.func
}

// Wrapper class to consume legacy context and pass to functional component
export class EditCobranding extends Component {
  render() {
    const {showModal, ...otherProps} = this.props

    return (
      <EditCobrandingContent
        {...otherProps}
        isModalVisible={showModal}
        modalContext={this.context}
      />
    )
  }
}

EditCobranding.propTypes = {showModal: PropTypes.bool}

EditCobranding.contextTypes = MODAL_CONTEXT_TYPES

export default modalWrapper(
  bindUpdate(EditCobranding, 'cobrandings', 'cobranding_url_string')
)
